import * as THREE from "three";
import { DEFAULT_ENV, type MapDef, type RaidEnv, type TrackSnap } from "@rietz/shared";
import { GlassPanes } from "../render/glass.js";
import { buildMapMesh, type BuiltMapMesh } from "../render/mapMesh.js";
import {
  EXTRACT_EDGE_COLOR,
  EXTRACT_FILL_COLOR,
  FOG_COLOR,
  FOG_DENSITY,
  GRID_COLOR,
  SKY_COLOR,
  lightingFor,
  type LightingPreset,
} from "../render/palette.js";
import { buildProps, mapVisuals, type BuiltProps } from "../render/props.js";
import { createPrecipitation, type PrecipKind, type RainField } from "../render/rain.js";
import { SHADOWS_ENABLED } from "../render/shadows.js";
import { buildSigns, type BuiltSigns } from "../render/signs.js";
import { createTrackField, type TrackField } from "../render/tracks.js";
import {
  applyLighting,
  buildSky,
  createLights,
  type BuiltLights,
  type BuiltSky,
  type SkyOptions,
} from "../render/sky.js";
import { buildPoiLabels, type PoiLabels } from "./poiLabels.js";

/**
 * Scene construction and the render target. `WorldView` owns the renderer,
 * camera, fog and lights — what outlives a map — and delegates geometry to
 * `render/`. `loadMap` is idempotent; everything it built is torn down first.
 * Per map: sky dome + mountain ring, merged solids, tracks, instanced props,
 * window panes, signposts, extraction volumes, POI labels.
 *
 * Shadows are off by default (`?shadows=1`, read once in `render/shadows.ts`)
 * — a render experiment that must not reach game logic or the protocol.
 * `SKY_COLOR` is re-exported for old call sites.
 */
export { SKY_COLOR };

/**
 * Clear colour for an indoor map. Not black — pure black makes every seam read
 * as a hole; near-black with a trace of blue reads as unlit concrete.
 */
const INDOOR_CLEAR_COLOR = 0x05060a;

/** What a map load may be told about the map beyond the map itself. */
export interface LoadMapOptions {
  /**
   * This map is enclosed: no dome, mountain ring, floor grid or label layers,
   * and clear to near-black. A flag, not a check on `map.id`.
   */
  indoor?: boolean;
}

export interface WorldViewOptions {
  canvas: HTMLCanvasElement;
  debug: boolean;
}

/**
 * A second scene drawn over the world with depth cleared — the viewmodel.
 * Structurally typed so `WorldView` owns passes, not weapons.
 */
export interface OverlayPass {
  scene: THREE.Scene;
  camera: THREE.Camera;
  /** Called on resize with the main camera's aspect, when present. */
  setAspect?(aspect: number): void;
  /**
   * Called before the world is drawn — where a scoped viewmodel renders its
   * picture-in-picture into a target. Optional. MUST restore the render target.
   */
  preRender?(renderer: THREE.WebGLRenderer, worldScene: THREE.Scene): void;
}

/** What the current map costs, for the debug HUD and the smoke test. */
export interface RenderStats {
  mapId: string;
  solidCount: number;
  propCount: number;
  /** Breakable window panes — one instance each, one draw call for the lot. */
  paneCount: number;
  triangles: number;
  /** Static draw calls, excluding characters and the HUD. */
  drawCalls: number;
}

export class WorldView {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;

  private readonly debug: boolean;
  private readonly lights: BuiltLights;

  /** Drawn after the world, with a cleared depth buffer. See `OverlayPass`. */
  private overlay: OverlayPass | null = null;

  /** Everything belonging to the currently loaded map. */
  private mapRoot: THREE.Group | null = null;
  private built: BuiltMapMesh | null = null;
  private props: BuiltProps | null = null;
  /** The breakable panes, or `null` on a map that has none (the hideout). */
  private panes: GlassPanes | null = null;
  private signs: BuiltSigns | null = null;
  private sky: BuiltSky | null = null;
  /** What the current sky was built from, so `setEnv` can rebuild it in place
   *  without the map being reloaded. `null` while no map is loaded. */
  private skyOpts: SkyOptions | null = null;
  /**
   * Time of day and weather, and the preset derived from it. A clear day until
   * `welcome` says otherwise.
   */
  private env: RaidEnv = DEFAULT_ENV;
  private preset: LightingPreset = lightingFor(DEFAULT_ENV);
  /** Falling rain or snow, or `null` in every weather that has neither. */
  private precip: RainField | null = null;
  /** Marks on the ground (protocol v29). Owned by the map, because it is the
   *  map's terrain the prints are tilted to. `null` while no map is loaded. */
  private tracks: TrackField | null = null;
  private poiLabels: PoiLabels | null = null;
  /** Sticky across map loads, like the debug toggle that drives it. */
  private poiShowAll = false;
  /**
   * The current map is enclosed. Read where the clear colour is written
   * (`applyClearColor`).
   */
  private indoor = false;
  private extraDisposers: (() => void)[] = [];

  private stats: RenderStats = {
    mapId: "",
    solidCount: 0,
    propCount: 0,
    paneCount: 0,
    triangles: 0,
    drawCalls: 0,
  };

  constructor(opts: WorldViewOptions) {
    this.debug = opts.debug;

    this.renderer = new THREE.WebGLRenderer({
      canvas: opts.canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setClearColor(SKY_COLOR, 1);
    if (SHADOWS_ENABLED) {
      // Set before anything is drawn — `shadowMap.enabled` is baked into every
      // compiled program. PCF-soft: a hard edge is the sharpest thing on
      // flat-shaded boxes and draws the eye to its own aliasing.
      this.renderer.shadowMap.enabled = true;
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    this.scene = new THREE.Scene();
    // Matched to the sky dome's horizon band, so distant geometry dissolves
    // into haze instead of into a coloured wall.
    this.scene.fog = new THREE.FogExp2(FOG_COLOR, FOG_DENSITY);

    this.camera = new THREE.PerspectiveCamera(75, 1, 0.05, 900);
    // MUST match `stepMovement`: yaw about Y applied before pitch about X.
    this.camera.rotation.order = "YXZ";

    this.lights = createLights();
    this.scene.add(this.lights.group);

    this.resize();
  }

  get renderStats(): Readonly<RenderStats> {
    return this.stats;
  }

  /** The current map's panes, or `null` where there are none. */
  get glass(): GlassPanes | null {
    return this.panes;
  }

  /** Rebuilds all map geometry. Safe to call again on a map change. */
  loadMap(map: MapDef, opts: LoadMapOptions = {}): void {
    this.disposeMap();

    const indoor = opts.indoor === true;
    const root = new THREE.Group();
    root.name = `map:${map.id}`;

    // --- Backdrop: none indoors. `setEnv` tolerates that (it returns without
    // sky options). The clear colour is written AFTER `applyLighting`.
    this.indoor = indoor;
    if (!indoor) {
      const b = map.bounds;
      const spanX = b.max.x - b.min.x;
      const spanZ = b.max.z - b.min.z;
      const span = Math.max(spanX, spanZ);
      // The dome has to enclose the whole map with room to spare, and stay
      // inside the camera's far plane.
      const skyRadius = Math.min(this.camera.far * 0.92, Math.max(400, span * 6));
      // Kept so `setEnv` can repaint the dome for a second raid on the same
      // map.
      this.skyOpts = {
        radius: skyRadius,
        mountains: true,
        groundY: b.min.y,
        preset: this.preset,
      };
      const sky = buildSky(this.skyOpts);
      root.add(sky.group);
      this.sky = sky;
    }

    // --- Solids. Snow is baked into vertex colours, so the weather must be
    // known first — `setEnv` runs from `welcome` before any map loads.
    const built = buildMapMesh(map, { snow: this.env.weather === "snow" });
    root.add(built.group);
    this.built = built;

    // --- Tracks, on the map root (not the scene) so a map swap takes them
    // along.
    const tracks = createTrackField(map, this.env.weather);
    root.add(tracks.mesh);
    this.tracks = tracks;

    // --- Props -------------------------------------------------------------
    const visuals = mapVisuals(map);
    const props = buildProps(visuals);
    root.add(props.group);
    this.props = props;

    // --- Window panes: breakable glazing, one instance per `map.glass` entry.
    // Their frames are ordinary `casement` props built above.
    const glass = map.glass ?? [];
    const panes = glass.length > 0 ? new GlassPanes(glass) : null;
    if (panes !== null) root.add(panes.group);
    this.panes = panes;

    // --- Signposts: the diegetic half of the POI labels. Static. None indoors.
    const signs = indoor ? null : buildSigns(map);
    if (signs !== null) root.add(signs.group);
    this.signs = signs;

    // --- Floor grid, fallback only: a flat untextured floor gives the eye
    // nothing to move against, but props solve that where they exist. Never
    // indoors.
    const grid = !indoor && visuals.length === 0;
    if (grid) {
      root.add(this.makeGrid(map));
    }

    // --- Extraction volumes ------------------------------------------------
    for (const ex of map.extracts) {
      root.add(this.makeExtract(ex.id, ex.zone));
    }

    // --- POI labels: signposting, not decoration — how a player builds a
    // mental
    // map of near-identical houses. Empty without POIs or a DOM.
    const poiLabels = indoor ? null : buildPoiLabels(map);
    if (poiLabels !== null) {
      poiLabels.setShowAll(this.poiShowAll);
      root.add(poiLabels.group);
    }
    this.poiLabels = poiLabels;

    if (this.debug) {
      const axes = new THREE.AxesHelper(3);
      axes.position.set(0, 0.02, 0);
      root.add(axes);
      this.extraDisposers.push(() => {
        axes.dispose();
      });
    }

    this.scene.add(root);
    this.mapRoot = root;

    // Re-applied on every map load — the moment a raid's environment is
    // certain.
    applyLighting(this.lights, this.scene, this.renderer, this.preset);
    this.applyClearColor();
    this.syncPrecipitation();

    this.stats = {
      mapId: map.id,
      solidCount: map.solids.length,
      propCount: visuals.length,
      paneCount: glass.length,
      triangles:
        built.triangles +
        props.triangles +
        (signs?.triangles ?? 0) +
        (this.sky?.triangles ?? 0),
      drawCalls:
        built.drawCalls +
        props.drawCalls +
        (signs?.drawCalls ?? 0) +
        (panes === null ? 0 : 1) +
        (indoor ? 0 : 2) /* dome + mountains */ +
        map.extracts.length * 2 +
        (poiLabels?.labelCount ?? 0) +
        1 /* tracks */ +
        (grid ? 1 : 0),
    };
  }

/**
 * Hangs one more object off the CURRENT map, tied to the map's teardown — the
 * escape hatch for geometry a `MapDef` cannot express (the hideout's
 * furniture). `dispose` joins the map's list, so it cannot leak across a map
 * change; every builder's `dispose` is idempotent. A no-op with a warning
 * when no map is loaded.
 */
  attachToMap(obj: THREE.Object3D, dispose: () => void): void {
    const root = this.mapRoot;
    if (root === null) {
      console.warn("WorldView.attachToMap: no map is loaded");
      return;
    }
    root.add(obj);
    this.extraDisposers.push(dispose);
  }

/**
 * Throws the map away and leaves an empty scene — what an online client boots
 * into and returns to between raids. Idempotent.
 */
  clearMap(): void {
    this.disposeMap();
    // Back to the horizon: the next thing drawn is the menu behind the loading
    // screen, and an indoor map's near-black would leave that looking broken.
    this.indoor = false;
    this.applyClearColor();
    // The weather goes back to the menu's — a finished raid is not still
    // raining.
    this.setEnv(DEFAULT_ENV);
    this.stats = {
      mapId: "",
      solidCount: 0,
      propCount: 0,
      paneCount: 0,
      triangles: 0,
      drawCalls: 0,
    };
  }

  /**
   * Points the view at one raid's time of day and weather. Called from the
   * `welcome` handler BEFORE the map loads so the first frame of a night raid
   * is dark; again on a reconnect, which is what needs the dome repaint.
   * Idempotent. Does NOT repaint the ground: lying snow is baked into the map
   * mesh at build, and the only call after a load is a reconnect into the same
   * raid, whose weather has not changed.
   */
  setEnv(env: RaidEnv): void {
    if (env.time === this.env.time && env.weather === this.env.weather) return;
    this.env = { time: env.time, weather: env.weather };
    this.preset = lightingFor(this.env);

    applyLighting(this.lights, this.scene, this.renderer, this.preset);
    this.applyClearColor();
    this.syncPrecipitation();

    // The dome's gradient is baked into its vertices — a new sky is new
    // geometry.
    const opts = this.skyOpts;
    const root = this.mapRoot;
    if (opts === null || root === null) return;
    const old = this.sky;
    if (old !== null) {
      root.remove(old.group);
      old.dispose();
    }
    this.skyOpts = { ...opts, preset: this.preset };
    const sky = buildSky(this.skyOpts);
    root.add(sky.group);
    this.sky = sky;
  }

/**
 * The colour behind everything: `applyLighting` writes the horizon, and this
 * takes it back for an indoor map. Two writers in fixed order, so the shared
 * lighting rig never learns "indoors".
 */
  private applyClearColor(): void {
    this.renderer.setClearColor(this.indoor ? INDOOR_CLEAR_COLOR : this.preset.skyHorizon, 1);
  }

  /** The environment currently being painted. */
  get raidEnv(): Readonly<RaidEnv> {
    return this.env;
  }

/**
 * Creates, swaps or drops the precipitation field to match the weather. Keyed
 * on the KIND — rain and snow are two parameter sets of one field.
 */
  private syncPrecipitation(): void {
    const w = this.env.weather;
    const wanted: PrecipKind | null = w === "rain" || w === "snow" ? w : null;
    if (wanted === (this.precip?.mesh.name ?? null)) return;
    this.precip?.dispose();
    this.precip = null;
    if (wanted === null) return;
    // Added to the SCENE and not to the map root: the weather follows the eye,
    // not the map, and it must survive a map swap the same way the lights do.
    const precip = createPrecipitation(wanted);
    this.scene.add(precip.mesh);
    this.precip = precip;
  }

/**
 * Stamps the marks one snapshot carried. A no-op before a map is loaded.
 * `nowSec` is `performance.now() / 1000`, the clock the field fades on.
 */
  addTracks(list: readonly TrackSnap[], nowSec: number): void {
    const field = this.tracks;
    if (field === null) return;
    for (const t of list) field.add(t, nowSec);
  }

/** Debug (F5): show every POI name regardless of distance. */
  setPoiLabelsShowAll(showAll: boolean): void {
    this.poiShowAll = showAll;
    this.poiLabels?.setShowAll(showAll);
  }

/** Installs (or clears) the overlay pass, syncing its aspect at once. */
  setOverlay(pass: OverlayPass | null): void {
    this.overlay = pass;
    pass?.setAspect?.(this.camera.aspect);
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    this.camera.aspect = h === 0 ? 1 : w / h;
    this.camera.updateProjectionMatrix();
    this.overlay?.setAspect?.(this.camera.aspect);
  }

  render(dtMs = 0): void {
    // Precipitation is a render-clock effect, so it takes the frame time.
    this.precip?.update(this.camera.position, dtMs / 1000);

    // ...and the tracks — one uniform the GPU fades every decal off, the same
    // clock `addTracks` gets.
    this.tracks?.update(performance.now() / 1000);

    // Keeps POI sprites at a constant apparent size beyond a threshold.
    this.poiLabels?.update(this.camera.position);

    // The sun's shadow box travels with the player. A no-op unless
    // `?shadows=1`.
    this.lights.updateShadowFocus(this.camera.position);

    // Off-screen passes the overlay needs (the scope picture), while the canvas
    // is untouched.
    this.overlay?.preRender?.(this.renderer, this.scene);

    this.renderer.render(this.scene, this.camera);

    const overlay = this.overlay;
    if (overlay === null) return;
    // `render` clears colour AND depth by default. Disable the auto clear,
    // throw
    // away only depth, draw — the overlay is unconditionally in front.
    this.renderer.autoClear = false;
    this.renderer.clearDepth();
    this.renderer.render(overlay.scene, overlay.camera);
    this.renderer.autoClear = true;
  }

  dispose(): void {
    this.disposeMap();
    this.precip?.dispose();
    this.precip = null;
    this.overlay = null;
    this.scene.remove(this.lights.group);
    this.lights.dispose();
    this.renderer.dispose();
  }

  // ---------------------------------------------------------------------

  /** Subtle ground reference for prop-less maps. */
  private makeGrid(map: MapDef): THREE.Object3D {
    const b = map.bounds;
    const span = Math.max(b.max.x - b.min.x, b.max.z - b.min.z);
    const grid = new THREE.GridHelper(span, Math.round(span), GRID_COLOR, GRID_COLOR);
    grid.position.set(
      (b.min.x + b.max.x) * 0.5,
      0.012,
      (b.min.z + b.max.z) * 0.5,
    );
    grid.material.transparent = true;
    grid.material.opacity = 0.16;
    grid.material.depthWrite = false;
    this.extraDisposers.push(() => {
      grid.geometry.dispose();
      grid.material.dispose();
    });
    return grid;
  }

  /**
   * An extraction zone: a faint additive volume plus bright edges — the edges
   * are what reads at distance.
   */
  private makeExtract(id: string, zone: MapDef["extracts"][number]["zone"]): THREE.Object3D {
    const sx = zone.max.x - zone.min.x;
    const sy = zone.max.y - zone.min.y;
    const sz = zone.max.z - zone.min.z;

    const group = new THREE.Group();
    group.name = `extract:${id}`;
    group.position.set(
      (zone.min.x + zone.max.x) * 0.5,
      (zone.min.y + zone.max.y) * 0.5,
      (zone.min.z + zone.max.z) * 0.5,
    );

    const geo = new THREE.BoxGeometry(sx, sy, sz);
    const fill = new THREE.MeshBasicMaterial({
      color: EXTRACT_FILL_COLOR,
      transparent: true,
      opacity: 0.1,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
    });
    const volume = new THREE.Mesh(geo, fill);
    group.add(volume);

    const edgeGeo = new THREE.EdgesGeometry(geo);
    const edgeMat = new THREE.LineBasicMaterial({
      color: EXTRACT_EDGE_COLOR,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    group.add(new THREE.LineSegments(edgeGeo, edgeMat));

    this.extraDisposers.push(() => {
      geo.dispose();
      fill.dispose();
      edgeGeo.dispose();
      edgeMat.dispose();
    });
    return group;
  }

  private disposeMap(): void {
    const root = this.mapRoot;
    if (root === null) return;
    this.scene.remove(root);

    this.built?.dispose();
    this.props?.dispose();
    this.panes?.dispose();
    this.signs?.dispose();
    this.sky?.dispose();
    this.tracks?.dispose();
    this.poiLabels?.dispose();
    for (const d of this.extraDisposers) d();

    this.built = null;
    this.props = null;
    this.panes = null;
    this.signs = null;
    this.sky = null;
    this.tracks = null;
    this.skyOpts = null;
    this.poiLabels = null;
    this.extraDisposers = [];
    root.clear();
    this.mapRoot = null;
  }
}
